import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import type { InteractionRow } from '../lib/types';
import { act, formatNumber } from '../lib/actions';
import { Button, duration, Panel } from './ui';

const STATUS: Record<string, string> = {
  completed: 'Completed',
  wrap_up: 'In wrap-up',
  abandoned: 'Abandoned',
  failed: 'Not connected',
  connected: 'Live',
  queued: 'Waiting',
  ringing: 'Ringing',
  ivr: 'In IVR',
  voicemail: 'Voicemail',
};

export function History() {
  const role = useNova((s) => s.user?.role);
  const [rows, setRows] = useState<InteractionRow[] | null>(null);
  const [direction, setDirection] = useState<'' | 'inbound' | 'outbound' | 'internal'>('');
  const [number, setNumber] = useState('');
  const [playing, setPlaying] = useState<{ id: string; url: string } | null>(null);

  useEffect(() => {
    let live = true;
    const qs = new URLSearchParams({ limit: '100' });
    if (direction) qs.set('direction', direction);
    if (number.replace(/\D/g, '').length >= 3) qs.set('number', number.replace(/\D/g, ''));
    const t = window.setTimeout(() => {
      api<InteractionRow[]>('GET', `/interactions?${qs}`)
        .then((r) => live && setRows(r))
        .catch(() => live && setRows([]));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [direction, number]);

  async function play(id: string) {
    const r = await act(() => api<{ url: string }>('GET', `/interactions/${id}/recording-url`));
    if (r) setPlaying({ id, url: r.url });
  }

  return (
    <Panel className="p-5">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium">{role === 'agent' ? 'Your calls' : 'Call history'}</h1>
          <p className="text-sm text-ink-3">Newest first, with recordings where the queue records.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            placeholder="Search a number"
            inputMode="tel"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className="tabular rounded-xl border border-line bg-abyss/60 px-3.5 py-2 text-sm outline-none focus:border-ion"
          />
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as typeof direction)}
            className="rounded-xl border border-line bg-abyss/60 px-3 py-2 text-sm outline-none focus:border-ion"
          >
            <option value="">All calls</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="internal">Internal</option>
          </select>
        </div>
      </div>

      {playing ? (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-line p-3">
          <audio src={playing.url} controls autoPlay className="h-10 flex-1" />
          <Button tone="ghost" onClick={() => setPlaying(null)}>
            Close
          </Button>
        </div>
      ) : null}

      {rows === null ? (
        <p className="py-10 text-center text-sm text-ink-3">Loading calls…</p>
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-ink-2">No calls match. Calls appear here as soon as they end.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="text-ink-3">
              <tr className="border-b border-line">
                <th className="py-2.5 pr-4 font-normal">When</th>
                <th className="py-2.5 pr-4 font-normal">Customer</th>
                <th className="py-2.5 pr-4 font-normal">Line</th>
                <th className="py-2.5 pr-4 font-normal">Agent</th>
                <th className="py-2.5 pr-4 font-normal text-right">Wait</th>
                <th className="py-2.5 pr-4 font-normal text-right">Talk</th>
                <th className="py-2.5 pr-4 font-normal">Outcome</th>
                <th className="py-2.5 font-normal" aria-label="Recording" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-line/60 last:border-0">
                  <td className="py-3 pr-4 text-ink-2">{new Date(r.started_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</td>
                  <td className="py-3 pr-4">
                    {/* An internal call: the colleague called, by name and extension */}
                    <span className="block">{(r.direction === 'internal' ? r.peer_name : r.customer_name) ?? formatNumber(r.customer_number ?? '')}</span>
                    {r.customer_name || r.direction === 'internal' ? (
                      <span className="tabular text-xs text-ink-3">{r.direction === 'internal' ? `Ext. ${r.customer_number}` : formatNumber(r.customer_number ?? '')}</span>
                    ) : null}
                  </td>
                  <td className="py-3 pr-4 text-ink-2">{r.direction === 'outbound' ? 'Outbound' : r.direction === 'internal' ? 'Internal' : r.queue_name ?? 'Inbound'}</td>
                  <td className="py-3 pr-4 text-ink-2">{r.agent_name ?? 'Nobody'}</td>
                  <td className="tabular py-3 pr-4 text-right text-ink-2">{r.wait_seconds != null ? duration(r.wait_seconds) : ''}</td>
                  <td className="tabular py-3 pr-4 text-right">{r.talk_seconds ? duration(r.talk_seconds) : ''}</td>
                  <td className="py-3 pr-4">
                    <span className={r.status === 'abandoned' || r.status === 'failed' ? 'text-break' : ''}>{r.disposition_label ?? STATUS[r.status] ?? r.status}</span>
                  </td>
                  <td className="py-3 text-right">
                    {r.has_recording ? (
                      <Button tone="ghost" className="px-2.5 py-1 text-xs" onClick={() => void play(r.id)} aria-pressed={playing?.id === r.id}>
                        Play
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
